import type React from "react";
import type { MutableRefObject } from "react";
import { useCallback, useContext, useEffect, useRef } from "react";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import type { GridApiCommon } from "@mui/x-data-grid";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMatch, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";

import type { Filter, Filters } from "routes/patients/PatientsList/PatientsFilter/useFilters";
import { BaseButton } from "shared/atoms/BaseButton";
import { FilterTag } from "shared/atoms/FilterTag";
import { Search } from "shared/molecules/Search";
import { PatientsFilterContext } from "utils/contexts";
import { useQueryParams } from "utils/hooks/useQueryParams/useQueryParams";
import { joinUrl } from "utils/url/joinUrl";

interface Props {
  apiRef: MutableRefObject<GridApiCommon>;
  patientCount: number;
  totalPatients: number;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onFilterClick: () => void;
}

const DISPLAY_FILTER_LIMIT = 2;

export const PatientsToolbar: React.FC<Props> = ({
  apiRef,
  patientCount,
  totalPatients,
  searchTerm,
  onSearchChange,
  onFilterClick,
}) => {
  const { t } = useTranslation();
  const match = useMatch("*");
  const navigate = useNavigate();
  const params = useQueryParams();
  const theme = useTheme();
  const { register, setValue } = useForm();
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const { activeFilters, toggleFilter, setFilter, filterLabel } = useContext<Filters>(PatientsFilterContext);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = useCallback(
    async (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    },
    [onSearchChange]
  );

  const closeFilterTag = (key: Filter) => {
    switch (key) {
      case "primary_joint":
        setFilter("primary_joint", { active: false, selection: [] });
        break;
      default:
        toggleFilter(key);
    }
    if (params.get("filter") === key && match) {
      params.delete("filter");
      navigate(joinUrl(match?.pathname, params));
    }
  };
  const renderedTags = activeFilters.slice(0, DISPLAY_FILTER_LIMIT).map((key: Filter) => {
    return <FilterTag key={key} close={() => closeFilterTag(key)} label={filterLabel(key)} />;
  });

  return (
    <StyledGridToolbar>
      <ToolbarLeft>
        <CountText>
          {patientCount === totalPatients
            ? `${totalPatients} ${t("views.patients")}`
            : `${patientCount} / ${totalPatients} ${t("views.patients")}`}
        </CountText>

        <Search
          icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
          inputStyle={{
            height: 40,
            fontSize: "14px",
            borderRadius: 8,
            border: `1px solid ${theme.colors.redesign.db20}`,
            width: "100%",
            minWidth: 150,
            maxWidth: 300,
          }}
          {...register("search")}
          name="patientSearch"
          reset={() => {
            setValue("search", "");
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }
            onSearchChange("");
          }}
          placeholder={t("patients.search")}
          search={searchTerm}
          onChange={e => handleSearchChange(e.target.value)}
        />
      </ToolbarLeft>

      <ToolbarRight>
        <Chips>
          {renderedTags}
          {activeFilters.length > DISPLAY_FILTER_LIMIT && (
            <MoreCount>
              <Typography variant="subtitle1">+{activeFilters.length - DISPLAY_FILTER_LIMIT}</Typography>
            </MoreCount>
          )}
        </Chips>

        <Stack direction="row" spacing={1}>
          <BaseButton variant="action" onClick={onFilterClick} text={t("form.filter")} icon="sliders-simple" />

          <BaseButton
            variant="action"
            onClick={() => {
              // @ts-expect-error depends on MUI X version
              apiRef.current.showPreferences?.("columns");
            }}
            text={t("form.columns")}
            icon="columns-3"
          />
        </Stack>
      </ToolbarRight>
    </StyledGridToolbar>
  );
};

const StyledGridToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: transparent;
  margin-bottom: 16px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  ${props => props.theme.belowMobileBreakpoint} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  ${props => props.theme.belowMobileBreakpoint} {
    align-items: flex-start;
    flex-direction: column-reverse;
  }
`;

const CountText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.redesign.db100};
  white-space: nowrap;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .MuiChip-root {
    padding: 4px 8px;
    height: 40px;
    border-radius: 50px;
  }
`;

const MoreCount = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background-color: ${props => props.theme.colors.redesign.b10};
  border-radius: 50px;
`;
